import { useState, useEffect, useCallback, useMemo } from 'react';
import { calculatorState as store } from 'store';
import { deviceTypedConfigFields } from './constants';
import { parseColumnsToStore } from './utils';

export function useInitialStateEffect(id, sections) {
  useEffect(() => {
    const modal = {
      preview: { isClosed: false, show: false },
      show: false,
    };
    const currentList = new Map();
    const { info, total } = sections.reduce(
      (acc, { name, type, blockType, items, defaultValue }) => {
        const _acc = { ...acc };

        if (blockType === 'columns') {
          const parsed = parseColumnsToStore(items);

          _acc.info[name] = parsed.info;
        } else {
          _acc.info[name] = {};

          items.forEach(item => {
            _acc.info[name][item] = defaultValue;
          });
        }

        if (type && !_acc.total[type]) {
          _acc.total[type] = [name];
        } else if (type) {
          _acc.total[type].push(name);
        }

        return _acc;
      },
      {
        info: {},
        total: {},
      },
    );

    store.setCalculator(id, { info, total, currentList, modal });

    return () => store.destroyCalculator(id);
  }, []);
}

export function useToggleCalculators() {
  const [state, setState] = useState({ deviceType: null });

  const changeWindowHandler = useCallback(event => {
    setState(prev => ({ ...prev, deviceType: event.matches ? 'desktop' : 'mobile' }));
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 545px)');

    const windowResizeHandler = () => {
      setState(prev => ({ ...prev, deviceType: window.innerWidth >= 657 ? 'desktop' : 'mobile' }));
    };

    if (mediaQuery?.addEventListener) {
      mediaQuery.addEventListener('change', changeWindowHandler);
      setState(prev => ({ ...prev, deviceType: mediaQuery.matches ? 'desktop' : 'mobile' }));
    } else {
      window.addEventListener('resize', windowResizeHandler);
      windowResizeHandler();
    }

    return () => {
      if (mediaQuery.addEventListener) {
        mediaQuery.removeEventListener('change', changeWindowHandler);
      } else {
        window.removeEventListener('resize', windowResizeHandler);
      }
    };
  }, []);

  return state;
}

export const useCountersHandlers = (onChangeCount, defaultValue = 0) => {
  const increment = (key, value) => {
    const count = value + 1;

    onChangeCount(key, count > 99 ? 99 : count);
  };

  const decrement = (key, value) => {
    const count = value - 1;

    onChangeCount(key, count > defaultValue ? count : defaultValue);
  };

  return { increment, decrement };
};

const getPreparedField = (field, deviceType) => {
  if (typeof field === 'object') {
    return field[deviceType];
  }

  return field;
};

export const usePrepareConfig = (config, deviceType) => {
  return useMemo(() => {
    const total = config.sections.some(({ counterType }) => counterType === 'counter');
    const preparedConfig = { ...config, total };

    Object.keys(config).forEach(key => {
      if (deviceTypedConfigFields.includes(key)) {
        preparedConfig[key] = getPreparedField(config[key], deviceType);
      }
    });

    return preparedConfig;
  }, [deviceType]);
};

export const useRangeStepsState = (name, defaultStep, calculatorID) => {
  const [step, setStep] = useState(defaultStep);
  const currentList = [...store.calculators[calculatorID].currentList];

  useEffect(() => {
    const currentStep = currentList.find(([, { sectionName }]) => sectionName === name);

    if (currentStep) {
      setStep(currentStep[1].value);
    } else {
      setStep(defaultStep);
    }
  }, [currentList.length]);

  return [step, setStep];
};
